import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  UseFormStateReturn,
} from 'react-hook-form';

import { BaseInputProps } from '../../../types';
import { Checkbox, FileInput, TextAreaStyled } from '../../Typhography';
import { ErrorMessage } from '../../Typhography/ErrorMessage';
import { InputStyled, Label, PlaceHolderIcon } from '../../Typhography/InputStyled';
import { RelativeInputDiv } from '../../Typhography/RelativeInputDiv';

interface InputProps<TFieldValues extends FieldValues> extends BaseInputProps<TFieldValues> {
  placeholder?: string;
  labelAdditionalDescription?: string;
  id?: string;
  type?:
    | 'text'
    | 'textarea'
    | 'password'
    | 'file'
    | 'checkbox'
    | 'email'
    | 'number'
    | 'tags'
    | 'radio';
  renderRightComponent?: () => React.ReactElement | null;
  wrapperHeight?: string;
  label?: string;
  labelOnClick?: () => void;
  autoComplete?: boolean;
  onKeyPress?: (event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  step?: string;
  minWidth?: string;
  maxWidth?: string;
  outerWidth?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChangeHook?: any;
  interval?: {
    min: number;
    max: number;
  };
  className?: string;
  maxOuterWidth?: string;
}

function Input<TFieldValues extends FieldValues>({
  id,
  placeholder,
  type = 'text',
  disabled,
  control,
  label,
  labelOnClick,
  error,
  name,
  checked,
  defaultValue = '',
  width,
  defaultChecked,
  height,
  color,
  margin,
  placeholderIcon,
  wrapperHeight,
  autoComplete,
  renderRightComponent,
  onKeyPress,
  onClick,
  step,
  minWidth,
  maxWidth,
  outerWidth,
  onChangeHook,
  interval,
  className,
  maxOuterWidth,
}: InputProps<TFieldValues>) {
  const { message: errorMessage } = error ?? {};

  const renderControllerInput = ({
    field: { onChange, value },
  }: {
    field: ControllerRenderProps<TFieldValues>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<TFieldValues>;
  }) => (
    <RelativeInputDiv
      className={className}
      border={type !== 'checkbox' && type !== 'radio' ? '1px solid #c4c4c4' : 'none'}
      margin={margin}
      height={wrapperHeight}
      width={outerWidth}
      maxWidth={maxOuterWidth}
    >
      {label && (
        <Label
          htmlFor={name}
          {...(labelOnClick
            ? {
                onClick: labelOnClick,
              }
            : {})}
        >
          {label}
        </Label>
      )}
      <RelativeInputDiv
        width={width}
        height={height}
        overflow={type !== 'checkbox' && type !== 'radio' ? 'hidden' : 'default'}
      >
        {(() => {
          if (type === 'file')
            return (
              <FileInput
                placeholder={placeholder}
                type={type}
                disabled={disabled}
                value={value}
                onChange={onChange}
                id={id}
                name={name}
                autoComplete={autoComplete ? 'on' : 'off'}
                onKeyPress={onKeyPress}
              />
            );

          if (type === 'checkbox')
            return (
              <Checkbox
                onKeyPress={onKeyPress}
                onClick={onClick}
                color={color}
                placeholder={placeholder}
                type={type}
                checked={checked || value}
                name={name}
                defaultChecked={defaultChecked}
                disabled={disabled}
                onChange={onChange}
                id={id}
                autoComplete={autoComplete ? 'on' : 'off'}
              />
            );

          if (type === 'radio')
            return (
              <Checkbox
                onKeyPress={onKeyPress}
                onClick={onClick}
                color={color}
                placeholder={placeholder}
                type={type}
                value={defaultValue}
                checked={checked || value === defaultValue}
                name={name}
                defaultChecked={defaultChecked}
                disabled={disabled}
                onChange={onChange}
                id={id}
                autoComplete={autoComplete ? 'on' : 'off'}
              />
            );

          if (type === 'textarea')
            return (
              <TextAreaStyled
                placeholder={placeholder}
                disabled={disabled}
                value={value}
                onChange={onChange}
                id={id}
                name={name}
                width="100%"
                autoComplete={autoComplete ? 'on' : 'off'}
                onKeyPress={onKeyPress}
              />
            );

          if (type === 'password')
            return (
              <InputStyled
                placeholder={placeholder}
                type={type}
                disabled={disabled}
                value={value}
                onChange={(event) => {
                  onChange(event);
                  if (onChangeHook) onChangeHook(event);
                }}
                id={id}
                autoComplete={autoComplete ? 'on' : 'new-password'}
                onKeyPress={onKeyPress}
                step={step}
                minWidth={minWidth}
                maxWidth={maxWidth}
                min={interval?.min}
                max={interval?.max}
                name={name}
              />
            );

          return (
            <InputStyled
              placeholder={placeholder}
              type={type}
              disabled={disabled}
              value={value}
              onChange={(event) => {
                onChange(event);

                if (onChangeHook) onChangeHook(event);
              }}
              id={id}
              autoComplete={autoComplete ? 'on' : 'off'}
              onKeyPress={onKeyPress}
              step={step}
              minWidth={minWidth}
              maxWidth={maxWidth}
              min={interval?.min}
              max={interval?.max}
              name={name}
            />
          );
        })()}
        {placeholderIcon && <PlaceHolderIcon src={placeholderIcon} />}
      </RelativeInputDiv>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </RelativeInputDiv>
  );

  return (
    <>
      {renderRightComponent?.()}
      <Controller
        control={control}
        name={name}
        render={renderControllerInput}
        defaultValue={defaultValue}
      />
    </>
  );
}

export default Input;
